'use client';

import Link from '~/(tenant)/_components/tenant-link';
import Image from 'next/image';
import { useState, useEffect } from 'react';
import { usePathname } from 'next/navigation';
import { Menu, X, Calendar, Package } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '@kit/ui/utils';

import type { LandingPageHeaderProps } from '../../_lib/landing-page-layout-registry';
import { TrsHeaderAccountSection } from './header-account-section';

const navLinks = [
  { labelKey: 'landing:nav.theSpace', fallback: 'THE SPACE', href: '/#about' },
  { labelKey: 'landing:nav.ourFacilities', fallback: 'OUR FACILITIES', href: '/#services' },
  { labelKey: 'landing:nav.memberships', fallback: 'MEMBERSHIPS', href: '/memberships' },
  { labelKey: 'landing:nav.getInTouch', fallback: 'GET IN TOUCH', href: '/#contact' },
  { labelKey: 'landing:nav.bookNow', fallback: 'BOOK NOW', href: '/book' },
];

const authNavLinks = [
  { labelKey: 'landing:nav.myBookings', fallback: 'BOOKINGS', href: '/bookings', icon: Calendar },
  { labelKey: 'landing:nav.myPackages', fallback: 'MY PACKAGES', href: '/packages', icon: Package },
];

const solidNavbarRoutes = [
  '/book',
  '/auth',
  '/bookings',
  '/profile',
  '/packages',
  '/payment',
  '/contact',
  '/privacy-policy',
  '/terms-of-service',
  '/event',
  '/coaches',
  '/membership',
  '/memberships',
];

export function TrsHeader(_props: LandingPageHeaderProps) {
  const { t } = useTranslation();
  const pathname = usePathname();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const shouldBeSolid = solidNavbarRoutes.some((route) =>
    pathname?.startsWith(route),
  );
  const isHomePage = pathname === '/';

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && mobileMenuOpen) {
        setMobileMenuOpen(false);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [mobileMenuOpen]);

  const showSolidBackground = mobileMenuOpen || shouldBeSolid || scrolled;

  return (
    <>
    <header
      className={cn(
        'fixed top-0 left-0 right-0 z-50 transition-all duration-500',
        mobileMenuOpen
          ? 'bg-[var(--trs-green)] pt-3 pb-1'
          : showSolidBackground
            ? 'bg-[var(--trs-green)] py-3'
            : 'bg-transparent py-5',
      )}
    >
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-12 flex items-center justify-between">
        {/* Logo */}
        <Link href="/" className="relative z-10 flex-shrink-0">
          <Image
            src="/landing-pages/trs/images/trs-logo.webp"
            alt="The Racquet Space"
            width={160}
            height={90}
            className={cn(
              'h-10 lg:h-12 w-auto',
              showSolidBackground && 'brightness-0 invert',
            )}
            priority
          />
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden lg:flex items-center">
          <div
            className={cn(
              'flex items-center gap-1 rounded-full px-2 py-1 transition-all duration-500',
              showSolidBackground
                ? 'bg-white/10'
                : 'bg-[var(--trs-green)]/20 backdrop-blur-sm',
            )}
          >
            <Link
              href="/"
              className={cn(
                'relative px-2.5 py-1.5 text-lg font-semibold tracking-[0.1em] transition-all duration-300 text-white hover:opacity-100',
                isHomePage ? 'opacity-100' : 'opacity-70',
              )}
            >
              HOME
              {isHomePage && (
                <span className="absolute bottom-2 left-5 right-5 h-[1px] bg-current" />
              )}
            </Link>

            {navLinks.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                className="relative px-2.5 py-1.5 text-lg font-semibold tracking-[0.1em] transition-all duration-300 text-white hover:opacity-100 opacity-70"
              >
                {t(link.labelKey, { defaultValue: link.fallback })}
              </Link>
            ))}

            {!isAuthenticated && (
              <a
                href="/auth/sign-in"
                className={cn(
                  'ml-2 px-5 py-2 rounded-full text-lg font-semibold tracking-[0.1em] transition-all duration-300',
                  showSolidBackground
                    ? 'bg-white text-[var(--trs-green)] hover:bg-white/90'
                    : 'bg-white/90 text-[var(--trs-green)] hover:bg-white',
                )}
              >
                Login | Register
              </a>
            )}
          </div>

          {isAuthenticated && (
            <div className="ml-3 flex items-center gap-1">
              {authNavLinks.map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  className={cn(
                    'relative px-2.5 py-1.5 text-lg font-semibold tracking-[0.1em] transition-all duration-300 text-white hover:opacity-100',
                    pathname === link.href ? 'opacity-100' : 'opacity-70',
                  )}
                >
                  {t(link.labelKey, { defaultValue: link.fallback })}
                  {pathname === link.href && (
                    <span className="absolute bottom-2 left-4 right-4 h-[1px] bg-current" />
                  )}
                </Link>
              ))}
              <TrsHeaderAccountSection
                scrolled={showSolidBackground}
              />
            </div>
          )}
        </nav>

        {/* Mobile Menu Button */}
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="lg:hidden relative z-10 text-white p-2 -mr-2"
          aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
        >
          {mobileMenuOpen ? (
            <X className="w-6 h-6 text-white" />
          ) : (
            <Menu className="w-6 h-6 text-white" />
          )}
        </button>
      </div>

      {/* Full Screen Mobile Menu */}
      <div
        className={cn(
          'lg:hidden fixed inset-0 bg-[var(--trs-green)] transition-all duration-500 z-40',
          mobileMenuOpen
            ? 'opacity-100 visible'
            : 'opacity-0 invisible pointer-events-none',
        )}
      >
        <div className="absolute top-0 left-0 right-0 px-4 py-4 flex items-center justify-between">
          <Link href="/" onClick={() => setMobileMenuOpen(false)}>
            <Image
              src="/landing-pages/trs/images/trs-logo.webp"
              alt="The Racquet Space"
              width={120}
              height={67}
              className="h-auto w-[100px] brightness-0 invert"
            />
          </Link>
          <button
            onClick={() => setMobileMenuOpen(false)}
            className="p-2"
            aria-label="Close menu"
          >
            <X className="w-7 h-7 text-white" />
          </button>
        </div>

        <div className="flex flex-col items-center justify-center h-full gap-6">
          <Link
            href="/"
            onClick={() => setMobileMenuOpen(false)}
            className={cn(
              'text-white text-2xl tracking-[0.2em] hover:text-[var(--trs-orange)] transition-colors',
              isHomePage && 'text-[var(--trs-orange)]',
            )}
          >
            HOME
          </Link>

          {navLinks.map((link) => (
            <Link
              key={link.href}
              href={link.href}
              onClick={() => setMobileMenuOpen(false)}
              className="text-white text-2xl tracking-[0.2em] hover:text-[var(--trs-orange)] transition-colors"
            >
              {t(link.labelKey, { defaultValue: link.fallback })}
            </Link>
          ))}

          {isAuthenticated &&
            authNavLinks.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                onClick={() => setMobileMenuOpen(false)}
                className={cn(
                  'text-white text-2xl tracking-[0.2em] hover:text-[var(--trs-orange)] transition-colors',
                  pathname === link.href && 'text-[var(--trs-orange)]',
                )}
              >
                {t(link.labelKey, { defaultValue: link.fallback })}
              </Link>
            ))}

          <div className="mt-6">
            {isAuthenticated ? (
              <TrsHeaderAccountSection
                scrolled={true}
                onNavigate={() => setMobileMenuOpen(false)}
                onAuthChange={setIsAuthenticated}
              />
            ) : (
              <a
                href="/auth/sign-in"
                onClick={() => setMobileMenuOpen(false)}
                className="px-8 py-3 bg-[var(--trs-orange)] text-white rounded-full text-xl tracking-[0.1em] hover:bg-[#D57E50] transition-colors"
              >
                Login | Register
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Auth state listener */}
      <TrsHeaderAccountSection
        hidden
        onAuthChange={setIsAuthenticated}
      />
    </header>
    {/* Spacer for fixed header - only on non-landing pages */}
    {!isHomePage && <div className="h-[64px]" />}
    </>
  );
}
